import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useAuth } from './AuthContext'
import { kiteAPI, isApiPaused } from '../services/api'
import toast from 'react-hot-toast'

const WebSocketContext = createContext()

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth()
  const [connected, setConnected] = useState(false)
  const [liveData, setLiveData] = useState({})
  // Global symbol-keyed tick map — keyed by SYMBOL (e.g. 'RELIANCE'), updated by any tick source
  const [symbolTicks, setSymbolTicks] = useState({})
  const [fallbackActive, setFallbackActive] = useState(false)
  const [subscribedSymbols, setSubscribedSymbols] = useState([])
  // Set to true when Kite sends a 403 — cleared when user manually restarts
  const [kiteStreamStopped, setKiteStreamStopped] = useState(false)
  const [kiteStreamStopReason, setKiteStreamStopReason] = useState('')
  // Set to true when AngelOne circuit breaker opens (3 failures in 15 s)
  const [streamPaused, setStreamPaused] = useState(false)
  const [streamPausedReason, setStreamPausedReason] = useState('')
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const fallbackIntervalRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 3

  useEffect(() => {
    if (user) {
      connectWebSocket()
    } else {
      disconnectWebSocket()
    }

    return () => {
      disconnectWebSocket()
    }
  }, [user])

  // Fallback polling function
  const startFallbackPolling = () => {
    if (fallbackIntervalRef.current || subscribedSymbols.length === 0) return
    
    setFallbackActive(true)
    console.log('🔄 Starting fallback API polling for', subscribedSymbols.length, 'symbols')
    
    const pollData = async () => {
      if (isApiPaused()) return  // back off during rate-limit pause
      try {
        const symbolsToFetch = subscribedSymbols.slice(0, 50) // Limit to 50 symbols per request
        if (symbolsToFetch.length === 0) return
        
        const response = await kiteAPI.getBulkQuote(symbolsToFetch.join(','))
        const quotes = response.data.data || {}
        
        // Convert Kite API format to our internal format
        Object.keys(quotes).forEach(key => {
          const quote = quotes[key]
          if (quote) {
            const instrumentToken = quote.instrument_token || key.replace('NSE:', '')
            setLiveData(prev => ({
              ...prev,
              [instrumentToken]: {
                instrumentToken,
                symbol: key.replace('NSE:', ''),
                exchange: 'NSE',
                ltp: quote.last_price || 0,
                volume: quote.volume || 0,
                change: quote.net_change || 0,
                changePercent: quote.change || 0,
                high: quote.ohlc?.high || quote.last_price || 0,
                low: quote.ohlc?.low || quote.last_price || 0,
                open: quote.ohlc?.open || quote.last_price || 0,
                close: quote.ohlc?.close || quote.last_price || 0,
                timestamp: new Date(),
                source: 'fallback'
              }
            }))
          }
        })
      } catch (error) {
        console.error('Fallback polling error:', error)
      }
    }
    
    // Poll immediately, then every 5 seconds
    pollData()
    fallbackIntervalRef.current = setInterval(pollData, 5000)
  }
  
  const stopFallbackPolling = () => {
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current)
      fallbackIntervalRef.current = null
      setFallbackActive(false)
      console.log('🛑 Stopped fallback API polling')
    }
  }
  const connectWebSocket = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    const token = localStorage.getItem('token')
    if (!token) return

    try {
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws?token=${token}`
      wsRef.current = new WebSocket(wsUrl)

      wsRef.current.onopen = () => {
        console.log('📡 WebSocket connected')
        setConnected(true)
        reconnectAttempts.current = 0 // Reset on successful connection
        stopFallbackPolling() // Stop fallback when WebSocket connects
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }))
          } else {
            clearInterval(pingInterval)
          }
        }, 30000)
      }

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          handleWebSocketMessage(data)
        } catch (error) {
          console.error('WebSocket message parse error:', error)
        }
      }

      wsRef.current.onclose = (event) => {
        console.log('📡 WebSocket disconnected:', event.code, event.reason)
        setConnected(false)
        
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }

        // Attempt to reconnect if not a normal closure (1000)
        if (event.code !== 1000) {
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++ // Increment attempt count
            const delay = 10 * 1000 // Fixed 10-second delay
            // Reconnect silently — no toast (was noisy on every action). Logged only.
            console.debug(`[ws] Connection lost. Retrying… (${reconnectAttempts.current}/${maxReconnectAttempts})`)
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket()
            }, delay)
          } else {
            // Max reconnect attempts reached
            toast.error('Failed to connect after multiple attempts. Please check your internet connection.')
            reconnectAttempts.current = 0 // Reset for future manual attempts
            // Start fallback polling when WebSocket fails completely
            if (subscribedSymbols.length > 0) {
              toast('Switching to API polling for live data', { icon: '📶' })
              startFallbackPolling()
            }
          }
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('📡 WebSocket error:', error)
        // Removed immediate error toast - will be handled by onclose after retry attempts
      }

    } catch (error) {
      console.error('WebSocket connection error:', error)
      toast.error('Failed to initiate WebSocket connection.')
    }
  }

  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
      reconnectTimeoutRef.current = null
    }
    reconnectAttempts.current = 0 // Reset attempts on explicit disconnect
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected')
      wsRef.current = null
    }
    reconnectAttempts.current = 0 // Reset attempts on explicit disconnect
    
    stopFallbackPolling()
    setConnected(false)
    setLiveData({})
  }

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'connection':
        if (data.status === 'connected') {
          console.log('📡 WebSocket connection confirmed')
        }
        break

      case 'tick':
        // Update live price data (keyed by token for legacy consumers)
        if (data.data) {
          const tick = { ...data.data, source: data.data.source || 'websocket' }
          setLiveData(prev => ({
            ...prev,
            [tick.instrument_token || tick.symbol]: tick
          }))
          // Also update the global symbol-keyed map for all tabs
          if (tick.symbol) {
            setSymbolTicks(prev => ({ ...prev, [tick.symbol]: tick }))
          }
        }
        break

      case 'kite_stream_stopped':
        // Only show Kite-specific messaging when the user's provider is actually Kite.
        // AngelOne users should never see this — the backend now prevents it, but guard here too.
        if (user?.streamingProvider !== 'angelone') {
          console.warn('Kite stream stopped:', data.message)
          setKiteStreamStopped(true)
          setKiteStreamStopReason(data.message || 'Kite session expired')
          toast.error('Kite stream stopped: session expired. Reconnect from the Streaming tab.', { duration: 6000 })
        }
        break

      case 'stream_paused':
        // Circuit breaker fired — too many failures in a short window
        console.warn('Stream paused by circuit breaker:', data.message)
        setStreamPaused(true)
        setStreamPausedReason(data.message || 'Streaming paused after repeated failures')
        toast.error(data.message || 'Streaming paused — too many failures. Restart from the Streaming tab.', { duration: 8000 })
        break

      case 'error':
        console.error('WebSocket error:', data.message)
        toast.error(data.message || 'WebSocket error')
        break

      case 'kite_connected':
        console.log('Kite streaming connected')
        break

      case 'subscription_success':
        console.log('Subscription successful:', data.message)
        break

      case 'data_saved':
        console.log('Data saved to database:', data.message)
        break

      case 'signal':
        // New signal notification
        toast.success(`New ${data.data.signalType} signal for ${data.data.symbol}`)
        break

      case 'order':
        // Order update notification
        toast(`Order ${data.data.status}: ${data.data.tradingsymbol}`)
        break

      case 'pong':
        // Ping response - connection is alive
        break

      default:
        console.log('Unknown WebSocket message type:', data.type)
    }
  }

  const restartKiteStream = () => {
    setKiteStreamStopped(false)
    setKiteStreamStopReason('')
    if (subscribedSymbols.length > 0) {
      subscribe(subscribedSymbols)
    }
  }

  const clearStreamPaused = () => {
    setStreamPaused(false)
    setStreamPausedReason('')
  }

  const subscribe = (instruments) => {
    // Clear stopped flag on any new subscription attempt
    setKiteStreamStopped(false)
    // Store subscribed symbols for fallback
    const symbols = Array.isArray(instruments) ?
      instruments.map(inst => typeof inst === 'string' ? inst.replace('NSE:', '') : inst) :
      []
    setSubscribedSymbols(symbols)
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        instruments
      }))
      stopFallbackPolling() // Stop fallback when WebSocket is working
    } else {
      // Start fallback immediately if WebSocket is not available
      if (symbols.length > 0) {
        console.log('📡 WebSocket not available, starting fallback polling')
        startFallbackPolling()
      }
    }
  }

  const unsubscribe = (instruments) => {
    setSubscribedSymbols([])
    stopFallbackPolling()
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe',
        instruments
      }))
    }
  }

  // Helper: get LTP for a symbol from ANY source (AngelOne tick or Kite fallback)
  const getLTP    = (symbol) => symbolTicks[symbol]?.ltp    ?? null
  const getOHLC   = (symbol) => {
    const t = symbolTicks[symbol]
    return t ? { open: t.open, high: t.high, low: t.low, close: t.close } : null
  }
  const getVolume = (symbol) => symbolTicks[symbol]?.volume ?? null
  const getTick   = (symbol) => symbolTicks[symbol]         ?? null

  const value = {
    connected,
    fallbackActive,
    liveData,
    symbolTicks,
    getLTP,
    getOHLC,
    getVolume,
    getTick,
    kiteStreamStopped,
    kiteStreamStopReason,
    streamPaused,
    streamPausedReason,
    clearStreamPaused,
    subscribe,
    unsubscribe,
    restartKiteStream,
    reconnect: connectWebSocket
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}